/** @jsx jsx */
import { jsx, InterpolationWithTheme } from '@emotion/core';
import log from 'loglevel';
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { map } from 'ramda';

import useTranslate from 'hooks/useTranslate';

import { Theme } from 'features/Theme';
import loadScript from 'utils/loadScript';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import { selectUnifiedIssuance, mergeIssuance } from 'state/unifiedIssuance';

import BreadPushProvisioningSDK, {
  GoogleOptions,
  AppleOptions,
  PushProvisionResult,
  GoogleResultDetails,
  AppleResultDetails,
} from 'utils/breadPushProvisioningSDK';

import useFinancingGoogleWalletProvision, {
  FinancingGoogleWalletProvisionError,
} from 'hooks/requests/financingGoogleWalletProvision';

import useFinancingAppleWalletProvision, {
  FinancingAppleWalletProvisionError,
} from 'hooks/requests/financingAppleWalletProvision';

import FinancingWalletButton from './FinancingWalletButton';
import useFinancingWalletButton from './useFinancingWalletButton';
import styles from './styles';
import { selectTenant } from 'state/tenant';
import { CircularProgress } from '@material-ui/core';

/**
 * Convenient method be notify when a ref is available by combining useRef and
 * useState.
 */
const useRefState = function<T>() {
  const [hasRef, setHasRef] = useState(false);
  const ref = useRef<T | null>(null);
  const refCallback = useCallback((element: T) => {
    ref.current = element;
    setHasRef(true);
  }, []);

  return [hasRef, ref, refCallback] as const;
};

const SCRIPT_PATHS = [
  `${process.env.PUBLIC_URL}/wallet/google.integration.min.js`,
  `${process.env.PUBLIC_URL}/wallet/initAddToAppleWallet.js`,
];

const useWalletScripts = () => {
  const [isReady, setIsReady] = useState(false);
  const loadWalletScripts = useCallback(async () => {
    await Promise.all(
      map(url => {
        return loadScript(url);
      }, SCRIPT_PATHS),
    );
    setIsReady(true);
    return;
  }, []);
  return { isReady, loadWalletScripts };
};

const useGoogleOptions = () => {
  const [provision] = useFinancingGoogleWalletProvision();
  const tenant = useSelector(selectTenant);
  const { googleIntegratorId } = tenant.enabledFeatures.financingWallet || {};

  const getOptions = useCallback(
    (
      button: HTMLButtonElement,
      issuanceId: string,
      onStart: () => void,
      onResult: (result: PushProvisionResult) => void,
    ): GoogleOptions => {
      if (!googleIntegratorId) {
        throw new Error('googleIntegratorId is missing.');
      }

      return {
        integratorId: googleIntegratorId,
        isTestEnvironment: !tenant.overrideConfig.production,
        tokenSetting: 1,
        cardSetting: 1,
        button: button,
        hl: 'en-US',
        onPushProvisionStart: onStart,
        onPushProvision: async params => {
          try {
            await provision({ ...params, issuanceId: issuanceId });
          } catch (e) {
            if (
              catchOnly(e, NonClientError, FinancingGoogleWalletProvisionError)
            ) {
              onResult('failure');
            }
          }
        },
        onResult,
      };
    },
    [googleIntegratorId, provision, tenant.overrideConfig.production],
  );
  return getOptions;
};

const useAppleOptions = () => {
  const [provision] = useFinancingAppleWalletProvision();
  const tenant = useSelector(selectTenant);
  const { applePartnerId } = tenant.enabledFeatures.financingWallet || {};

  const getOptions = useCallback(
    (
      button: HTMLButtonElement,
      issuanceId: string,
      onStart: () => void,
      onResult: (result: PushProvisionResult) => void,
    ): AppleOptions => {
      if (!applePartnerId) {
        throw new Error('applePartnerId is missing.');
      }

      return {
        partnerId: applePartnerId,
        button: button,
        onPushProvisionStart: onStart,
        onPushProvision: async () => {
          try {
            await provision({ issuanceId });
          } catch (e) {
            if (
              catchOnly(e, NonClientError, FinancingAppleWalletProvisionError)
            ) {
              onResult('failure');
            }
          }
        },
        onResult,
      };
    },
    [applePartnerId, provision],
  );
  return getOptions;
};

interface PropTypes {
  containerStyles: InterpolationWithTheme<Theme>;
  buttonContainerStyle: InterpolationWithTheme<Theme>;
}

const FinancingWallets: React.FC<PropTypes> = props => {
  const t = useTranslate();
  const { containerStyles, buttonContainerStyle } = props;
  const dispatch = useDispatch();

  const getWalletsButtonState = useFinancingWalletButton();
  const buttons = useMemo(() => getWalletsButtonState(), [
    getWalletsButtonState,
  ]);

  const { isReady: scriptReady, loadWalletScripts } = useWalletScripts();

  const { issuanceId, pushProvisionResult } = useSelector(
    selectUnifiedIssuance,
  );
  if (!issuanceId) {
    throw new Error('issuanceId must be present');
  }
  const [hasGoogleRef, googleRef, googleRefCb] = useRefState<
    HTMLButtonElement
  >();

  const [hasAppleRef, appleRef, appleRefCb] = useRefState<HTMLButtonElement>();

  const sdk = useRef<BreadPushProvisioningSDK | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const getGoogleOptions = useGoogleOptions();
  const getAppleOptions = useAppleOptions();

  const [provisionInProgress, setProvisionInProgress] = useState<boolean>(
    false,
  );

  const showResultSnackbar = useCallback(
    (result: PushProvisionResult) => {
      if (result === 'success') {
        enqueueSnackbar(t('financingPushProvision.message.success'), {
          variant: 'success',
        });
      } else if (result === 'cancel') {
        enqueueSnackbar(t('financingPushProvision.message.cancel'));
      } else if (result === 'unsupportedDevice') {
        enqueueSnackbar(t('financingPushProvision.message.unsupportedDevice'), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(t('financingPushProvision.message.error'), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, t],
  );

  const handleStart = useCallback(() => {
    setProvisionInProgress(true);
  }, []);

  const handleResult = useCallback(
    (
      result: PushProvisionResult,
      details?: GoogleResultDetails | AppleResultDetails,
    ) => {
      if (details) {
        log.info(details);
      }

      setProvisionInProgress(false);

      showResultSnackbar(result);
      dispatch(mergeIssuance({ pushProvisionResult: result }));
    },
    [dispatch, showResultSnackbar],
  );

  useEffect(() => {
    loadWalletScripts().catch(e => {
      log.error(e);
      handleResult('failure');
    });
  }, [enqueueSnackbar, handleResult, loadWalletScripts]);

  useEffect(() => {
    const ready =
      scriptReady &&
      (!buttons.hasAppleButton || hasAppleRef) &&
      (!buttons.hasGoogleButton || hasGoogleRef);

    if (!ready) return;

    if (sdk.current) {
      log.error('SDK is being attempted to be instantiated again');
    }

    sdk.current = new BreadPushProvisioningSDK({
      google: buttons.hasGoogleButton
        ? getGoogleOptions(
            googleRef.current as HTMLButtonElement,
            issuanceId,
            handleStart,
            handleResult,
          )
        : undefined,

      apple: buttons.hasAppleButton
        ? getAppleOptions(
            appleRef.current as HTMLButtonElement,
            issuanceId,
            handleStart,
            handleResult,
          )
        : undefined,
    });
  }, [
    appleRef,
    buttons,
    getAppleOptions,
    getGoogleOptions,
    googleRef,
    handleResult,
    handleStart,
    hasAppleRef,
    hasGoogleRef,
    issuanceId,
    scriptReady,
  ]);

  if (!scriptReady) {
    return null;
  }

  if (!buttons.hasFinancingWalletButton) {
    return null;
  }

  /**
   * Only allow provisioning once
   */
  if (pushProvisionResult === 'success') {
    return null;
  }

  return (
    <React.Fragment>
      {provisionInProgress ? (
        <div css={containerStyles}>
          <CircularProgress aria-label="progress" size={24} />
        </div>
      ) : null}
      <div style={{ display: provisionInProgress ? 'none' : 'unset' }}>
        <div css={containerStyles}>
          {buttons.hasAppleButton && (
            <div css={buttonContainerStyle}>
              <FinancingWalletButton
                id="appleButton"
                ref={appleRefCb}
                css={styles.walletButton}
                vendor="apple"
              />
            </div>
          )}
          {buttons.hasGoogleButton && (
            <div css={buttonContainerStyle}>
              <FinancingWalletButton
                ref={googleRefCb}
                css={styles.walletButton}
                vendor="google"
              />
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default FinancingWallets;
